import {
  AlertCircle,
  ArrowRight,
  Building07,
  Mail01,
  PlusCircle,
  Settings01,
  Users01,
} from '@untitledui/icons';
import type { OpenInvitation, Team } from 'api-client';
import { OpenInvitationFromJSON } from 'api-client';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { fetchWithDefaults } from '@/lib/http/client';

import { useUser } from '../allauth_auth/hooks.js';
import { useOrg } from '../contexts/OrgContext';
import { useAvailableOrgs } from '../hooks/useAvailableOrgs';
import {
  ADMIN_PERMISSION_GROUP,
  hasEntityPermission,
  hasPermissionGroup,
} from '../hooks/usePermissions';
import { useTitle } from '../hooks/useTitle';

// Shared focus/interaction classes for the pressable AriaButton rows in
// TeamRow and the create-org footer row — keep both in sync when editing.
const PRESSABLE_ROW_FOCUS_CLASSES =
  'text-left cursor-pointer outline-focus-ring focus-visible:outline-2 focus-visible:-outline-offset-2';

interface InvitationRowProps {
  invitation: OpenInvitation;
  loading: boolean;
  onInvitationAction: (id: number, action: 'accept' | 'decline') => void;
}

function InvitationRow({
  invitation,
  loading,
  onInvitationAction,
}: InvitationRowProps) {
  const handleAccept = useCallback(
    () => onInvitationAction(invitation.id, 'accept'),
    [onInvitationAction, invitation.id]
  );
  const handleDecline = useCallback(
    () => onInvitationAction(invitation.id, 'decline'),
    [onInvitationAction, invitation.id]
  );

  return (
    <div className='flex items-center justify-between p-4 bg-white dark:bg-bg-secondary rounded-lg border border-border-secondary'>
      <div>
        <h3 className='font-medium text-primary'>{invitation.teamName}</h3>
        <p className='text-sm text-tertiary truncate'>
          Invited by {invitation.invitedByName} · {invitation.invitedByEmail}
        </p>
      </div>
      <div className='flex gap-2'>
        <Button
          color='primary'
          size='sm'
          onClick={handleAccept}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Accept'}
        </Button>
        <Button
          color='secondary'
          size='sm'
          onClick={handleDecline}
          disabled={loading}
        >
          Decline
        </Button>
      </div>
    </div>
  );
}

interface PendingInvitationsProps {
  invitations: OpenInvitation[];
  actionLoading: { [key: number]: boolean };
  onInvitationAction: (
    invitationId: number,
    action: 'accept' | 'decline'
  ) => void;
}

export function PendingInvitations({
  invitations,
  actionLoading,
  onInvitationAction,
}: PendingInvitationsProps) {
  if (invitations.length === 0) {
    return null;
  }

  return (
    <div className='bg-warning-50 dark:bg-bg-secondary border border-warning-200 rounded-xl shadow-sm overflow-hidden mt-8'>
      <div className='px-6 py-5 border-b border-warning-200'>
        <div className='flex items-center gap-3'>
          <FeaturedIcon
            icon={Mail01}
            size='sm'
            theme='dark-outline'
            color='warning'
          />
          <div>
            <h2 className='text-lg font-semibold text-secondary'>
              Pending Invitations
            </h2>
            <p className='text-sm text-tertiary'>
              You have {invitations.length} organization invitation
              {invitations.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>
      <div className='p-6'>
        <div className='space-y-3'>
          {invitations.map(invitation => (
            <InvitationRow
              key={invitation.id}
              invitation={invitation}
              loading={actionLoading[invitation.id] ?? false}
              onInvitationAction={onInvitationAction}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface TeamRowProps {
  team: Team;
  onSelect: (orgId: number) => void;
  onManage: (orgId: number) => void;
}

function TeamRow({ team, onSelect, onManage }: TeamRowProps) {
  const handleSelect = useCallback(
    () => onSelect(team.id),
    [onSelect, team.id]
  );
  const handleManageClick = useCallback(
    () => onManage(team.id),
    [onManage, team.id]
  );

  return (
    <div className='group flex items-stretch hover:bg-bg-secondary_hover transition-colors'>
      <AriaButton
        onPress={handleSelect}
        className={`flex flex-1 min-w-0 items-center justify-between gap-4 p-6 ${PRESSABLE_ROW_FOCUS_CLASSES}`}
      >
        <div className='flex items-center gap-4 min-w-0'>
          <FeaturedIcon
            icon={Building07}
            size='md'
            theme='modern'
            color='gray'
          />
          <div className='min-w-0'>
            <h3 className='text-lg font-semibold text-primary group-hover:text-fg-brand-primary transition-colors truncate'>
              {team.name}
            </h3>
            <div className='flex items-center gap-4 mt-1'>
              <div className='flex items-center gap-1 text-sm text-tertiary'>
                <Users01 className='w-4 h-4' />
                <span>{team.memberCount ?? 0} members</span>
              </div>
              {hasPermissionGroup(
                team.permissionGroups,
                ADMIN_PERMISSION_GROUP
              ) && (
                <Badge type='modern' color='gray' size='sm'>
                  Admin
                </Badge>
              )}
            </div>
          </div>
        </div>
        <ArrowRight className='w-5 h-5 shrink-0 text-fg-quaternary group-hover:text-fg-brand-primary transition-colors' />
      </AriaButton>
      {hasEntityPermission(
        team.permissions as Array<{
          entityTypeName: string;
          eventTypeName: string;
        }>,
        'ahoy_user'
      ) && (
        <div className='flex items-center pr-6'>
          <Dropdown.Root>
            <Dropdown.DotsButton />
            <Dropdown.Popover>
              <Dropdown.Menu>
                <Dropdown.Item icon={Settings01} onAction={handleManageClick}>
                  Manage
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown.Root>
        </div>
      )}
    </div>
  );
}

export function OrganizationsPage() {
  const {
    availableOrgs: orgsFromContext,
    isLoading,
    error,
    hasLoadedSuccessfully,
    switchOrg,
    refreshCurrentOrg,
  } = useOrg();
  const user = useUser();
  const { data: cachedOrgs = [] } = useAvailableOrgs(user?.id);
  // Use the in-memory OrgContext value once it has settled (i.e. loadTeams
  // has completed at least once). Until then, fall back to the
  // localStorage-backed cache for the initial pre-load paint to avoid
  // flicker. Gating on `hasLoadedSuccessfully` instead of `length > 0`
  // ensures a genuinely empty live result (e.g. user removed from every
  // org) is respected and not masked by a stale persisted snapshot.
  const availableOrgs = hasLoadedSuccessfully ? orgsFromContext : cachedOrgs;
  const [userInvitations, setUserInvitations] = useState<OpenInvitation[]>([]);
  const [actionLoading, setActionLoading] = useState<{
    [key: number]: boolean;
  }>({});
  const canCreateOrg = user?.is_staff === true;

  useTitle('Select Organization');
  const navigate = useNavigate();

  useEffect(() => {
    const loadInvitations = async () => {
      try {
        const response = await fetchWithDefaults('/orgs/api/user-invitations/');
        if (response.ok) {
          const data = await response.json();
          setUserInvitations((data.results || []).map(OpenInvitationFromJSON));
        }
      } catch (err) {
        console.error('Error loading invitations:', err);
        // Don't block UI on invitation fetch failure
      }
    };
    loadInvitations();
  }, []);

  const handleInvitationAction = useCallback(
    async (invitationId: number, action: 'accept' | 'decline') => {
      try {
        setActionLoading(prev => ({ ...prev, [invitationId]: true }));

        if (action === 'accept') {
          // Use the invitation accept API endpoint
          const response = await fetchWithDefaults(
            `/api/invitations/${invitationId}/`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            }
          );
          if (!response.ok) {
            throw new Error('Failed to accept invitation');
          }
          await refreshCurrentOrg(); // Refresh orgs list after accepting
        }
        // For decline: just remove from local state (no backend API for decline - invitation expires naturally)

        // Remove from local state
        setUserInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      } catch (err) {
        console.error(`Error ${action}ing invitation:`, err);
        toast.error(`We couldn't ${action} that invitation. Try again.`);
      } finally {
        setActionLoading(prev => ({ ...prev, [invitationId]: false }));
      }
    },
    [refreshCurrentOrg]
  );

  const handleTeamSelect = useCallback(
    async (orgId: number) => {
      try {
        await switchOrg(orgId);
        navigate(`/${orgId}`);
      } catch (err) {
        console.error('Error switching team:', err);
      }
    },
    [switchOrg, navigate]
  );

  const handleManage = useCallback(
    async (orgId: number) => {
      try {
        await switchOrg(orgId);
        navigate(`/${orgId}/settings/team-management`);
      } catch (err) {
        console.error('Error switching to org for management:', err);
      }
    },
    [switchOrg, navigate]
  );

  // Synchronous: navigates without switching orgs, unlike handleTeamSelect/handleManage
  const handleCreateOrg = useCallback(
    () => navigate('/create-organization'),
    [navigate]
  );

  if (isLoading) {
    return (
      <div className='min-h-full bg-bg-primary flex items-center justify-center'>
        <LoadingIndicator size='lg' />
      </div>
    );
  }

  if (error) {
    return (
      <div className='min-h-full bg-bg-primary flex items-center justify-center'>
        <div className='max-w-md text-center'>
          <div className='flex justify-center mb-4'>
            <FeaturedIcon
              icon={AlertCircle}
              size='xl'
              theme='light-outline'
              color='error'
            />
          </div>
          <h2 className='text-display-sm font-semibold text-primary'>
            Something went wrong
          </h2>
          <p className='text-md text-tertiary mt-2'>{error}</p>
          <Button
            color='primary'
            size='lg'
            className='mt-6'
            onClick={() => window.location.reload()}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (availableOrgs.length === 0) {
    if (!canCreateOrg) {
      // User cannot create orgs - show waiting state
      return (
        <div className='min-h-full bg-bg-primary flex items-center justify-center px-4'>
          <div className='mx-auto max-w-md w-full'>
            <div className='text-center'>
              {/* Logo */}
              <div className='flex justify-center mb-12'>
                <img
                  src='/ahoy-logo-with-text-black.svg'
                  alt='Ahoy'
                  className='h-12 dark:invert'
                />
              </div>

              {/* Icon */}
              <div className='flex justify-center mb-6'>
                <FeaturedIcon
                  icon={Building07}
                  size='xl'
                  theme='modern'
                  color='brand'
                />
              </div>

              {/* Content */}
              <h1 className='text-display-md font-semibold text-primary mb-3'>
                Welcome to Ahoy
              </h1>
              <p className='text-md text-tertiary mb-8'>
                Your account is set up and ready. You'll be able to access Ahoy
                once an organization admin invites you to their team.
              </p>
            </div>

            {/* Pending Invitations */}
            <PendingInvitations
              invitations={userInvitations}
              actionLoading={actionLoading}
              onInvitationAction={handleInvitationAction}
            />
          </div>
        </div>
      );
    } else {
      // User can create orgs - show default CTA
      return (
        <div className='min-h-full bg-bg-primary flex items-center justify-center px-4'>
          <div className='mx-auto max-w-md w-full'>
            <div className='text-center'>
              {/* Logo */}
              <div className='flex justify-center mb-12'>
                <img
                  src='/ahoy-logo-with-text-black.svg'
                  alt='Ahoy'
                  className='h-12 dark:invert'
                />
              </div>

              {/* Icon */}
              <div className='flex justify-center mb-6'>
                <FeaturedIcon
                  icon={Building07}
                  size='xl'
                  theme='modern'
                  color='brand'
                />
              </div>

              {/* Content */}
              <h1 className='text-display-md font-semibold text-primary mb-3'>
                Welcome to Ahoy
              </h1>
              <p className='text-md text-tertiary mb-8'>
                Create your first organization to start collaborating with your
                team and unlock powerful AI-driven workflows.
              </p>

              {/* CTA */}
              <Button
                color='primary'
                size='xl'
                iconLeading={<PlusCircle className='w-5 h-5' />}
                onClick={() => navigate('/create-organization')}
                className='w-full'
              >
                Create Your First Organization
              </Button>
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <div className='min-h-full bg-bg-primary flex items-center justify-center px-4 pt-4 pb-16'>
      <div className='mx-auto max-w-3xl w-full'>
        {/* Header */}
        <div className='flex items-center justify-between mb-8'>
          {/* Logo */}
          <img
            src='/ahoy-logo-with-text-black.svg'
            alt='Ahoy'
            className='h-10 dark:invert'
          />

          {/* Title */}
          <div className='text-right'>
            <h1 className='text-xl font-semibold text-primary mb-1'>
              Select Organization
            </h1>
            <p className='text-sm text-tertiary'>
              You're a member of multiple organizations. Which one would you
              like to access?
            </p>
          </div>
        </div>

        {/* Pending Invitations */}
        {userInvitations.length > 0 && (
          <div className='mb-8'>
            <PendingInvitations
              invitations={userInvitations}
              actionLoading={actionLoading}
              onInvitationAction={handleInvitationAction}
            />
          </div>
        )}

        {/* Team List */}
        <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'>
          {availableOrgs.map((team, index) => (
            <Fragment key={team.id}>
              {index > 0 && <hr className='border-t border-border-secondary' />}
              <TeamRow
                team={team}
                onSelect={handleTeamSelect}
                onManage={handleManage}
              />
            </Fragment>
          ))}

          {/* Create new org footer */}
          {canCreateOrg && (
            <>
              <hr className='border-t border-border-secondary' />
              <AriaButton
                onPress={handleCreateOrg}
                className={`group flex w-full items-center justify-between p-6 hover:bg-bg-secondary_hover transition-colors ${PRESSABLE_ROW_FOCUS_CLASSES}`}
              >
                <div className='flex items-center gap-4'>
                  <FeaturedIcon
                    icon={PlusCircle}
                    size='md'
                    theme='light-outline'
                    color='brand'
                  />
                  <div>
                    <h3 className='text-lg font-semibold text-fg-brand-primary'>
                      Create new organization
                    </h3>
                    <p className='text-sm text-tertiary mt-0.5'>
                      Start a new organization and invite your team
                    </p>
                  </div>
                </div>
                <ArrowRight className='w-5 h-5 text-fg-brand-primary' />
              </AriaButton>
            </>
          )}
        </div>

        {/* Hint for joining other orgs */}
        <p className='text-sm text-tertiary text-center mt-6'>
          Looking to join another organization? Ask an admin of that
          organization to send you an invite.
        </p>
      </div>
    </div>
  );
}

// Backwards compatibility export
export { OrganizationsPage as FullPageTeamSwitcher };
